import { type FormEvent, useEffect, useRef, useState } from "react";
import { create } from "zustand";
import { useSession } from "@/app/session";
import { api } from "@/lib/api";
import { recentErrors } from "@/lib/errors";
import { useT } from "@/lib/i18n";
import { Alert, Button } from "./ui";

/**
 * "Report a problem" (test phase). Any page opens it with openFeedback(); it is mounted once in App.
 * Hidden entirely when the workspace switch (Settings → Reports) is off.
 */
const useFeedbackUI = create<{ open: boolean; boardId: string | null }>(() => ({
  open: false,
  boardId: null,
}));
export const openFeedback = (boardId: string | null = null) =>
  useFeedbackUI.setState({ open: true, boardId });
const closeFeedback = () => useFeedbackUI.setState({ open: false, boardId: null });
export const useFeedbackEnabled = () => !!useSession().me?.workspace.feedbackEnabled;

export function FeedbackButton({
  boardId = null,
  className = "",
}: {
  boardId?: string | null;
  className?: string;
}) {
  const t = useT();
  if (!useFeedbackEnabled()) return null;
  return (
    <button
      type="button"
      onClick={() => openFeedback(boardId)}
      className={`whitespace-nowrap text-muted hover:text-text ${className}`}
      title={t.feedback.hint}
    >
      {t.feedback.button}
    </button>
  );
}

export function FeedbackDialog() {
  const { open, boardId } = useFeedbackUI();
  const enabled = useFeedbackEnabled();
  const t = useT();
  const [message, setMessage] = useState("");
  const [expected, setExpected] = useState("");
  const [tech, setTech] = useState(true);
  const [busy, setBusy] = useState(false);
  const [state, setState] = useState<"form" | "sent" | "error">("form");
  const textRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (!open) return;
    setState("form");
    setMessage("");
    setExpected("");
    setTimeout(() => textRef.current?.focus(), 0);
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && closeFeedback();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);
  if (!open || !enabled) return null;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (message.trim().length < 5) return;
    setBusy(true);
    try {
      const errs = recentErrors();
      await api("/api/feedback", {
        method: "POST",
        json: {
          kind: "bug",
          message: message.trim(),
          expected: expected.trim() || undefined,
          ...(tech
            ? {
                page: location.href,
                boardId: boardId ?? undefined,
                version: typeof __APP_VERSION__ === "string" ? __APP_VERSION__ : "dev",
                userAgent: navigator.userAgent,
                viewport: `${window.innerWidth}x${window.innerHeight}@${devicePixelRatio}`,
                extra: [
                  `Language: ${navigator.language}`,
                  `Online: ${navigator.onLine}`,
                  `Touch: ${navigator.maxTouchPoints > 0}`,
                  ...(errs.length ? ["Recent browser errors:", ...errs] : []),
                ].join("\n"),
              }
            : {}),
        },
      });
      setState("sent");
    } catch {
      setState("error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4"
      role="dialog"
      aria-modal="true"
      onPointerDown={(e) => e.target === e.currentTarget && closeFeedback()}
    >
      <div className="w-full max-w-lg rounded-md border border-border bg-panel p-6 shadow-panel">
        <h2 className="display mb-1 text-3xl">.{t.feedback.title}</h2>
        {state === "sent" ? (
          <>
            <p className="mb-5 text-sm text-muted">{t.feedback.thanks}</p>
            <div className="flex justify-end">
              <Button onClick={closeFeedback}>{t.close}</Button>
            </div>
          </>
        ) : (
          <form onSubmit={submit} className="flex flex-col gap-3">
            <p className="text-sm text-muted">{t.feedback.intro}</p>
            {state === "error" ? <Alert kind="error">{t.feedback.failed}</Alert> : null}
            <label className="block">
              <span className="mb-1.5 block text-sm font-semibold">{t.feedback.what}</span>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                ref={textRef}
                rows={4}
                required
                minLength={5}
                maxLength={4000}
                placeholder={t.feedback.whatPlaceholder}
                className="w-full rounded-sm border border-border bg-panel-2 px-3 py-2 text-text placeholder:text-dim outline-none focus:border-accent"
              />
            </label>
            <label className="block">
              <span className="mb-1.5 block text-sm font-semibold">{t.feedback.expected}</span>
              <textarea
                value={expected}
                onChange={(e) => setExpected(e.target.value)}
                rows={2}
                maxLength={2000}
                className="w-full rounded-sm border border-border bg-panel-2 px-3 py-2 text-text placeholder:text-dim outline-none focus:border-accent"
              />
            </label>
            <label className="flex items-start gap-2 text-sm text-muted">
              <input
                type="checkbox"
                checked={tech}
                onChange={(e) => setTech(e.target.checked)}
                className="mt-1"
              />
              <span>{t.feedback.includeTech}</span>
            </label>
            <div className="mt-2 flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={closeFeedback}>
                {t.cancel}
              </Button>
              <Button type="submit" loading={busy} disabled={message.trim().length < 5}>
                {t.send}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
